// src/components/account/BusinessDetails.js
import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  UserIcon,
  MapPinIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';

const BusinessDetails = ({ data, onSave, saving }) => {
  const [formData, setFormData] = useState({
    registeredCompanyName: '',
    companyRegistrationNumber: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    registeredBusinessAddress: {
      line1: '',
      line2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'United Kingdom'
    },
    vatNumber: '',
    vatRegistered: false
  });

  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        registeredCompanyName: data.registeredCompanyName || '',
        companyRegistrationNumber: data.companyRegistrationNumber || '',
        primaryContactName: data.primaryContactName || '',
        primaryContactEmail: data.primaryContactEmail || '',
        primaryContactPhone: data.primaryContactPhone || '',
        registeredBusinessAddress: data.registeredBusinessAddress || {
          line1: '',
          line2: '',
          city: '',
          county: '',
          postcode: '',
          country: 'United Kingdom'
        },
        vatNumber: data.vatNumber || '',
        vatRegistered: data.vatRegistered || false
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      registeredBusinessAddress: {
        ...prev.registeredBusinessAddress,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.registeredCompanyName?.trim()) {
      newErrors.registeredCompanyName = 'Company name is required';
    }

    if (!formData.primaryContactName?.trim()) {
      newErrors.primaryContactName = 'Contact name is required';
    }

    if (!formData.primaryContactEmail?.trim()) {
      newErrors.primaryContactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryContactEmail)) {
      newErrors.primaryContactEmail = 'Invalid email format';
    }

    if (!formData.registeredBusinessAddress?.line1?.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.registeredBusinessAddress?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.registeredBusinessAddress?.postcode?.trim()) {
      newErrors.postcode = 'Postcode is required';
    }

    if (formData.vatRegistered && !formData.vatNumber?.trim()) {
      newErrors.vatNumber = 'VAT number is required when VAT registered';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
      setHasChanges(false);
    }
  };

  const formatVATNumber = (value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!cleaned.startsWith('GB') && cleaned.length >= 9) {
      return 'GB' + cleaned;
    }
    return cleaned;
  };

  // Common input style function
  const getInputStyle = (hasError) => ({
    width: '100%',
    padding: 'var(--input-padding-y) var(--input-padding-x)',
    fontSize: 'var(--font-size-body3)',
    border: `1px solid ${hasError ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'}`,
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--Surface-Primary)',
    color: 'var(--Text-Primary)',
    transition: 'all 0.2s ease',
    outline: 'none'
  });

  const handleInputFocus = (e, hasError) => {
    if (!hasError) {
      e.target.style.borderColor = 'var(--Input-Border-Focused)';
      e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
    }
  };

  const handleInputBlur = (e, hasError) => {
    if (!hasError) {
      e.target.style.borderColor = 'var(--Input-Border-Default)';
      e.target.style.boxShadow = 'none';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-l)' }}>
      {/* Company Details Section */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <BuildingOfficeIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Company Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--spacing-xl)' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Registered Company Name *
            </label>
            <input
              type="text"
              style={getInputStyle(errors.registeredCompanyName)}
              value={formData.registeredCompanyName}
              onChange={(e) => handleChange('registeredCompanyName', e.target.value)}
              placeholder="Your registered business name"
              onFocus={(e) => handleInputFocus(e, errors.registeredCompanyName)}
              onBlur={(e) => handleInputBlur(e, errors.registeredCompanyName)}
            />
            {errors.registeredCompanyName && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.registeredCompanyName}</p>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>
              Company Registration Number
            </label>
            <input
              type="text"
              style={getInputStyle(false)}
              value={formData.companyRegistrationNumber}
              onChange={(e) => handleChange('companyRegistrationNumber', e.target.value)}
              placeholder="e.g., 12345678"
              onFocus={(e) => handleInputFocus(e, false)}
              onBlur={(e) => handleInputBlur(e, false)}
            />
            <p style={{
              fontSize: 'var(--font-size-body5)',
              color: 'var(--Text-Tertiary)',
              marginTop: 'var(--spacing-xxs)'
            }}>Companies House number (optional)</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--Stroke-Light)' }}></div>

      {/* Primary Contact Section */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <UserIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Primary Contact</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 'var(--spacing-m)' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Contact Name *</label>
            <input
              type="text"
              style={getInputStyle(errors.primaryContactName)}
              value={formData.primaryContactName}
              onChange={(e) => handleChange('primaryContactName', e.target.value)}
              placeholder="Full name"
              onFocus={(e) => handleInputFocus(e, errors.primaryContactName)}
              onBlur={(e) => handleInputBlur(e, errors.primaryContactName)}
            />
            {errors.primaryContactName && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.primaryContactName}</p>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Contact Email *</label>
            <input
              type="email"
              style={getInputStyle(errors.primaryContactEmail)}
              value={formData.primaryContactEmail}
              onChange={(e) => handleChange('primaryContactEmail', e.target.value)}
              placeholder="email@example.com"
              onFocus={(e) => handleInputFocus(e, errors.primaryContactEmail)}
              onBlur={(e) => handleInputBlur(e, errors.primaryContactEmail)}
            />
            {errors.primaryContactEmail && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.primaryContactEmail}</p>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Contact Phone</label>
            <input
              type="tel"
              style={getInputStyle(false)}
              value={formData.primaryContactPhone}
              onChange={(e) => handleChange('primaryContactPhone', e.target.value)}
              placeholder="+44 20 1234 5678"
              onFocus={(e) => handleInputFocus(e, false)}
              onBlur={(e) => handleInputBlur(e, false)}
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--Stroke-Light)' }}></div>

      {/* Business Address Section */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <MapPinIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Registered Address</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Address Line 1 *</label>
            <input
              type="text"
              style={getInputStyle(errors.addressLine1)}
              value={formData.registeredBusinessAddress?.line1 || ''}
              onChange={(e) => handleAddressChange('line1', e.target.value)}
              placeholder="Street address"
              onFocus={(e) => handleInputFocus(e, errors.addressLine1)}
              onBlur={(e) => handleInputBlur(e, errors.addressLine1)}
            />
            {errors.addressLine1 && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.addressLine1}</p>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Address Line 2</label>
            <input
              type="text"
              style={getInputStyle(false)}
              value={formData.registeredBusinessAddress?.line2 || ''}
              onChange={(e) => handleAddressChange('line2', e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
              onFocus={(e) => handleInputFocus(e, false)}
              onBlur={(e) => handleInputBlur(e, false)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 'var(--spacing-m)' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>City *</label>
              <input
                type="text"
                style={getInputStyle(errors.city)}
                value={formData.registeredBusinessAddress?.city || ''}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder="City"
                onFocus={(e) => handleInputFocus(e, errors.city)}
                onBlur={(e) => handleInputBlur(e, errors.city)}
              />
              {errors.city && (
                <p style={{
                  fontSize: 'var(--font-size-body5)',
                  color: 'var(--Text-Error)',
                  marginTop: 'var(--spacing-xxs)'
                }}>{errors.city}</p>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>County</label>
              <input
                type="text"
                style={getInputStyle(false)}
                value={formData.registeredBusinessAddress?.county || ''}
                onChange={(e) => handleAddressChange('county', e.target.value)}
                placeholder="County"
                onFocus={(e) => handleInputFocus(e, false)}
                onBlur={(e) => handleInputBlur(e, false)}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>Postcode *</label>
              <input
                type="text"
                style={getInputStyle(errors.postcode)}
                value={formData.registeredBusinessAddress?.postcode || ''}
                onChange={(e) => handleAddressChange('postcode', e.target.value.toUpperCase())}
                placeholder="Postcode"
                onFocus={(e) => handleInputFocus(e, errors.postcode)}
                onBlur={(e) => handleInputBlur(e, errors.postcode)}
              />
              {errors.postcode && (
                <p style={{
                  fontSize: 'var(--font-size-body5)',
                  color: 'var(--Text-Error)',
                  marginTop: 'var(--spacing-xxs)'
                }}>{errors.postcode}</p>
              )}
            </div>
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Country</label>
            <input
              type="text"
              style={{
                ...getInputStyle(false),
                backgroundColor: 'var(--Surface-Secondary)',
                cursor: 'not-allowed'
              }}
              value={formData.registeredBusinessAddress?.country || 'United Kingdom'}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--Stroke-Light)' }}></div>

      {/* VAT Information Section */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <CalculatorIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>VAT Information</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
          <div className="flex items-center" style={{ gap: 'var(--spacing-s)' }}>
            <input
              type="checkbox"
              id="vatRegistered"
              style={{
                width: '16px',
                height: '16px',
                accentColor: 'var(--Purple-600)',
                borderRadius: 'var(--radius-xxs)',
                cursor: 'pointer'
              }}
              checked={formData.vatRegistered}
              onChange={(e) => handleChange('vatRegistered', e.target.checked)}
            />
            <label htmlFor="vatRegistered" style={{
              fontSize: 'var(--font-size-body3)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              cursor: 'pointer'
            }}>
              VAT Registered Business
            </label>
          </div>

          {formData.vatRegistered && (
            <div style={{
              animation: 'slideDown 0.2s ease-out'
            }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--Text-Primary)',
                marginBottom: 'var(--spacing-xs)'
              }}>VAT Number *</label>
              <input
                type="text"
                style={getInputStyle(errors.vatNumber)}
                value={formData.vatNumber}
                onChange={(e) => handleChange('vatNumber', formatVATNumber(e.target.value))}
                placeholder="GB123456789"
                onFocus={(e) => handleInputFocus(e, errors.vatNumber)}
                onBlur={(e) => handleInputBlur(e, errors.vatNumber)}
              />
              {errors.vatNumber && (
                <p style={{
                  fontSize: 'var(--font-size-body5)',
                  color: 'var(--Text-Error)',
                  marginTop: 'var(--spacing-xxs)'
                }}>{errors.vatNumber}</p>
              )}
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Tertiary)',
                marginTop: 'var(--spacing-xxs)'
              }}>Format: GB followed by 9 or 12 digits</p>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: 'var(--spacing-xl)',
        borderTop: '1px solid var(--Stroke-Light)'
      }}>
        <button
          onClick={handleSubmit}
          disabled={!hasChanges || saving}
          style={{
            padding: 'var(--button-padding-y) var(--button-padding-x)',
            background: (!hasChanges || saving) ? 'var(--Grey-200)' : 'var(--brand-gradient-1)',
            color: (!hasChanges || saving) ? 'var(--Text-Tertiary)' : 'var(--Text-Contrast)',
            border: 'none',
            borderRadius: 'var(--button-radius)',
            fontSize: 'var(--font-size-button)',
            fontWeight: 'var(--button-font-weight)',
            cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)',
            transition: 'all 0.2s ease',
            opacity: (!hasChanges || saving) ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (hasChanges && !saving) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-primary)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {saving ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default BusinessDetails;